import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { subscribeTopic } from './liveUpdates';
import { tx, useXomicsI18n } from './i18n';
import {
  XomicsApiError,
  changePassword as changePasswordRequest,
  clearAccessToken,
  getRolePreview,
  loadCurrentAccount,
  loadPreviewRoles,
  loadSessions as loadSessionsRequest,
  login as loginRequest,
  logout as logoutRequest,
  restoreSession,
  revokeSession as revokeSessionRequest,
  setRolePreview,
  updateProfile as updateProfileRequest,
} from './api';

const AuthContext = createContext(null);
const AUTH_SYNC_INTERVAL_MS = 5000;

function anonymousState() {
  return {
    status: 'anonymous',
    user: null,
    permissions: [],
  };
}

export function XomicsAuthProvider({ children }) {
  const { language, setLanguage } = useXomicsI18n();
  const [auth, setAuth] = useState({
    status: 'booting',
    user: null,
    permissions: [],
  });
  const [previewRoles, setPreviewRoles] = useState([]);
  const statusRef = useRef(auth.status);

  useEffect(() => {
    statusRef.current = auth.status;
  }, [auth.status]);

  const applyAuthentication = useCallback((payload) => {
    setAuth({
      status: 'authenticated',
      user: payload.user,
      permissions: Array.isArray(payload.permissions) ? payload.permissions : [],
    });
    if (payload.user?.preferredLanguage && payload.user.preferredLanguage !== language) {
      setLanguage(payload.user.preferredLanguage).catch(() => {});
    }
  }, [language, setLanguage]);

  const becomeAnonymous = useCallback(() => {
    clearAccessToken();
    setRolePreview(null);
    setPreviewRoles([]);
    setAuth(anonymousState());
  }, []);

  const synchronize = useCallback(async () => {
    if (statusRef.current !== 'authenticated') return;
    try {
      applyAuthentication(await loadCurrentAccount());
    } catch (error) {
      let requestError = error;
      if (requestError instanceof XomicsApiError
          && requestError.status === 403 && getRolePreview() !== null) {
        setRolePreview(null);
        try {
          applyAuthentication(await loadCurrentAccount());
          return;
        } catch (previewRecoveryError) {
          if (!(previewRecoveryError instanceof XomicsApiError)
              || ![401, 403].includes(previewRecoveryError.status)) {
            throw previewRecoveryError;
          }
          requestError = previewRecoveryError;
        }
      }
      if (
        requestError instanceof XomicsApiError
        && [401, 403].includes(requestError.status)
      ) {
        becomeAnonymous();
        return;
      }
      throw requestError;
    }
  }, [applyAuthentication, becomeAnonymous]);

  // An administrator changed your role, name or active state.
  //
  // 'session' is the one topic delivered to a single account rather than to
  // everyone entitled to a surface, and nothing about it arrives through a
  // screen's own refetch: the sidebar, the breadcrumb and every permission gate
  // read auth state, not a page's data. Subscribed here, once, on behalf of the
  // whole app — synchronize() re-reads /Auth/me and re-renders whatever depends
  // on it.
  //
  // The server has already demoted them: authorization resolves the role from
  // the database on every request. This is what stops the client going on
  // offering actions that would now be refused. Nobody is signed out.
  useEffect(() => subscribeTopic('session', () => {
    // A failure is not worth surfacing here: the token is either still good, in
    // which case the next call succeeds, or already dead, in which case the api
    // layer signs them out.
    synchronize().catch(() => {});
  }), [synchronize]);

  useEffect(() => {
    let active = true;
    restoreSession()
      .then(async (payload) => {
        if (!active) return;
        applyAuthentication(payload);
        if (getRolePreview() !== null) {
          try {
            const previewed = await loadCurrentAccount();
            if (active) applyAuthentication(previewed);
          } catch (error) {
            setRolePreview(null);
            if (active) applyAuthentication(payload);
          }
        }
      })
      .catch((error) => {
        if (!active) return;
        if (error instanceof XomicsApiError && [401, 403].includes(error.status)) {
          becomeAnonymous();
          return;
        }
        clearAccessToken();
        setAuth({
          ...anonymousState(),
          startupError: tx('ui.errors.service_unreachable'),
        });
      });

    return () => {
      active = false;
    };
  }, [applyAuthentication, becomeAnonymous]);

  useEffect(() => {
    if (auth.status !== 'authenticated') return undefined;
    let active = true;
    loadPreviewRoles()
      .then((roles) => { if (active) setPreviewRoles(roles); })
      .catch((error) => {
        if (active && error instanceof XomicsApiError && error.status === 403) {
          setPreviewRoles([]);
        }
      });
    return () => { active = false; };
  }, [auth.status, auth.user?.actualRoleCode]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      synchronize().catch(() => {});
    }, AUTH_SYNC_INTERVAL_MS);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        synchronize().catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [synchronize]);

  const login = useCallback(async (email, password) => {
    const payload = await loginRequest(email, password);
    applyAuthentication(payload);
    return payload;
  }, [applyAuthentication]);

  const logout = useCallback(async (allDevices = false) => {
    await logoutRequest({ allDevices });
    becomeAnonymous();
  }, [becomeAnonymous]);

  const updateProfile = useCallback(async (profile) => {
    await updateProfileRequest(profile);
    await synchronize();
  }, [synchronize]);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    await changePasswordRequest(currentPassword, newPassword);
    becomeAnonymous();
  }, [becomeAnonymous]);

  const loadSessions = useCallback(() => loadSessionsRequest(), []);

  const revokeSession = useCallback(async (sessionUuid) => {
    const result = await revokeSessionRequest(sessionUuid);
    if (result.currentSessionRevoked) becomeAnonymous();
    return result;
  }, [becomeAnonymous]);

  const previewAsRole = useCallback(async (roleCode) => {
    const previous = getRolePreview();
    setRolePreview(roleCode);
    try {
      const payload = await loadCurrentAccount();
      applyAuthentication(payload);
      return payload;
    } catch (error) {
      setRolePreview(previous);
      try {
        applyAuthentication(await loadCurrentAccount());
      } catch (_restoreError) {
        // The original error is the useful one. Normal auth recovery handles
        // a genuinely expired session on the next request.
      }
      throw error;
    }
  }, [applyAuthentication]);

  return (
    <AuthContext.Provider value={{
      ...auth,
      login,
      logout,
      synchronize,
      updateProfile,
      changePassword,
      loadSessions,
      revokeSession,
      previewRoles,
      previewAsRole,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useXomicsAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useXomicsAuth must be used inside XomicsAuthProvider');
  }
  return context;
}
